import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
  Fragment
} from "react";

import { useNavigate } from "react-router-dom";

import {
  motion,
  useInView,
  useScroll,
  useTransform
} from "framer-motion";

import "../styles/Home.css";

/* ================= ASSETS ================= */

import heroVeg from "../assets/images/hero-veg.png";
import heroJain from "../assets/images/hero-jain.jpeg";

import cloudKitchen from "../assets/images/cloud-kitchen.png";
import cateringEvent from "../assets/images/catering-event.png";
import chefCooking from "../assets/images/chef-cooking.png";

import comboHero from "../assets/images/combo-hero.jpeg";
import s1Hero from "../assets/images/s1-hero.png";
import s2Hero from "../assets/images/s2-hero.png";
import s3Hero from "../assets/images/s3-hero.png";

import Menus from "../components/Menus";

import logo from "../assets/images/sacs-logo.png";

/* ======================================================
DATA
===================================================== */

const bookingOptions = Object.freeze([
  {
    id: "CloudKitchen",
    title: "Daily Foods",
    description:
      "Fresh home-style vegetarian meals cooked daily with pure veg and Jain options.",
    highlight: "Lunch plans",
    route: "/cloud-kitchen",
    icon: "🍱",
    popular: true,
    image: cloudKitchen
  },
  {
    id: "catering",
    title: "Catering Services",
    description:
      "Professional South Indian catering for poojas, functions and corporate events.",
    highlight: "30 to 500 guests",
    route: "/catering",
    icon: "🎉",
    popular: false,
    image: cateringEvent
  },
  {
    id: "ChefBooking",
    title: "Madi Samayal Chef",
    description:
      "Private vegetarian chef cooking at your home with your ingredients.",
    highlight: "Ideal for poojas & family events",
    route: "/chef-booking",
    icon: "👨‍🍳",
    popular: false,
    image: chefCooking
  }
]);

const stats = Object.freeze([
  { label: "Happy Customers", value: "500+" },
  { label: "Years Served", value: "25+" },
  { label: "Events Catered", value: "300+" },
  { label: "Chef Hours", value: "10000+" }
]);

/* ================= HERO SLIDES ================= */

const heroSlides = Object.freeze([
  {
    id: 1,
    image: comboHero,
    title: "Traditional South Indian Vegetarian Excellence",
    fit: "contain"
  },
  {
    id: 2,
    image: s1Hero,
    title: "Sacred Veg Catering"
  },
  {
    id: 3,
    image: s2Hero,
    title: "Authentic Madi Samayal"
  },
  {
    id: 4,
    image: s3Hero,
    title: "Daily Meals & Cloud Kitchen"
  }
]);

/* ======================================================
HELPERS
===================================================== */

const SkeletonLoader = () => (
  <div className="loading-skeleton" aria-hidden="true">
    <div className="skeleton-hero" />
    <div className="skeleton-stats" />
    <div className="skeleton-services" />
    <div className="skeleton-menus" />
    <div className="skeleton-intro" />
  </div>
);

/* ======================================================
HOME
===================================================== */

const Home = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  const [activeSlide, setActiveSlide] = useState(0);
  const [isSliderPaused, setIsSliderPaused] = useState(false);
  const [openHeroSlide, setOpenHeroSlide] = useState(null);

  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll();

  const heroParallaxY = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "-18%"]
  );

  const heroInView = useInView(heroRef, {
    once: true
  });

  /* ================= PRELOAD ================= */

  useEffect(() => {
    let alive = true;

    const preload = async () => {
      const imgs = [
        heroVeg,
        heroJain,
        cloudKitchen,
        cateringEvent,
        chefCooking,
        comboHero,
        s1Hero,
        s2Hero,
        s3Hero
      ];

      await Promise.allSettled(
        imgs.map(
          src =>
            new Promise(res => {
              const img = new Image();

              img.onload = img.onerror = res;

              img.src = src;
            })
        )
      );

      if (alive) setIsLoading(false);
    };

    preload();

    return () => (alive = false);
  }, []);

  /* ================= HERO SLIDER ================= */

  useEffect(() => {
    if (isSliderPaused) return undefined;

    const interval = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % heroSlides.length);
    }, 4500);

    return () => clearInterval(interval);
  }, [isSliderPaused]);

  useEffect(() => {
    if (!openHeroSlide) return undefined;

    const handleEscape = event => {
      if (event.key === "Escape") {
        setOpenHeroSlide(null);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [openHeroSlide]);

  /* ================= NAVIGATION ================= */

  const routes = useMemo(
    () =>
      bookingOptions.reduce((a, s) => {
        a[s.id] = s.route;
        return a;
      }, {}),
    []
  );

  const goToService = useCallback(
    id => navigate(routes[id] || "/services"),
    [navigate, routes]
  );

  if (isLoading) return <SkeletonLoader />;

  return (
    <Fragment>
      <motion.main className="home" role="main">
        {/* ================= HERO ================= */}

        <section
          ref={heroRef}
          className="hero hero-vertical"
        >
          <div className="hero-overlay" />

          {/* ================= HERO CONTENT ================= */}

          <div className="hero-content hero-content-top">
            <motion.div
              className="hero-logo"
              initial={{ opacity: 0, y: -20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
            >
              <img
                src={logo}
                alt="SACS Pure Veg"
                className="hero-logo-img"
              />
            </motion.div>

            <motion.h1 className="hero-title">
              Taste of Tradition,
              <br />
              <span className="gradient-text">
                Delivered Fresh From Our Kitchen to Yours
              </span>
            </motion.h1>

            <p className="hero-subtitle">
              Pure vegetarian South Indian meals —
              daily food, catering & home chefs.
            </p>

            <div className="hero-actions">
              <button
                className="primaryBtn large"
                onClick={() =>
                  goToService("CloudKitchen")
                }
              >
                Order Daily Meals
              </button>

              <button
                className="secondaryBtn large"
                onClick={() => navigate("/Services")}
              >
                Explore Services
              </button>
            </div>
          </div>

          {/* ================= HERO IMAGE SLIDER ================= */}

          <motion.div
            className="hero-slider-container"
            style={{ y: heroParallaxY }}
          >
            <div className="hero-slider-frame">
              {heroSlides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`hero-slide ${
                    index === activeSlide
                      ? "active"
                      : ""
                  } ${
                    slide.fit === "contain"
                      ? "is-contain"
                      : ""
                  }`}
                  style={{
                    backgroundImage: `url(${slide.image})`
                  }}
                />
              ))}

              <div className="hero-slider-overlay" />

              <button
                className="hero-slide-open"
                onClick={() =>
                  setOpenHeroSlide(heroSlides[activeSlide])
                }
                aria-label={`Open ${heroSlides[activeSlide].title}`}
                type="button"
              />
            </div>

            {/* ================= BOTTOM CONTROLS ================= */}

            <div className="hero-slider-bottom-controls">
              <button
                className="hero-nav-btn"
                onClick={() =>
                  setActiveSlide(
                    prev =>
                      (prev - 1 + heroSlides.length) %
                      heroSlides.length
                  )
                }
                aria-label="Previous Slide"
              >
                ❮
              </button>

              <button
                className="hero-play-toggle"
                onClick={() =>
                  setIsSliderPaused(prev => !prev)
                }
                aria-label={
                  isSliderPaused
                    ? "Play Hero Slider"
                    : "Pause Hero Slider"
                }
                aria-pressed={isSliderPaused}
                type="button"
              >
                <span aria-hidden="true">
                  {isSliderPaused ? "▶" : "Ⅱ"}
                </span>
              </button>

              <button
                className="hero-nav-btn"
                onClick={() =>
                  setActiveSlide(
                    prev =>
                      (prev + 1) % heroSlides.length
                  )
                }
                aria-label="Next Slide"
              >
                ❯
              </button>
            </div>

            {/* ================= INDICATORS ================= */}

            <div className="hero-slide-indicators">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${
                    index === activeSlide
                      ? "active"
                      : ""
                  }`}
                  onClick={() =>
                    setActiveSlide(index)
                  }
                />
              ))}
            </div>
          </motion.div>
        </section>

        {/* ================= STATS ================= */}

        <section className="section statsSection">
          <div className="statsContainer">
            {stats.map(s => (
              <div
                key={s.label}
                className="statCard"
              >
                <div className="statNumber">
                  {s.value}
                </div>

                <div className="statLabel">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ================= SERVICES ================= */}

        <section className="section servicesSection">
          <h2 className="sectionTitle">
            How We Serve You
          </h2>

          <div className="servicesGrid">
            {bookingOptions.map(service => (
              <div
                key={service.id}
                className="serviceCard"
                onClick={() =>
                  goToService(service.id)
                }
              >
                <img
                  src={service.image}
                  alt={service.title}
                />

                <h3>{service.title}</h3>

                <p>{service.description}</p>

                <span className="serviceHighlight">
                  {service.highlight}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ================= BOOKING STEPS ================= */}

        <section className="section bookingStepsSection">
          <h2 className="sectionTitle">
            How Booking Works
          </h2>

          <p className="sectionSubtitle">
            Follow these exact steps based on how
            booking works in this app.
          </p>

          <div className="bookingStepsGrid">
            <div className="bookingStepsCard">
              <h3>Daily Foods</h3>

              <ol>
                <li>
                  Open Daily Foods and select your
                  meal plan.
                </li>

                <li>
                  Fill booking details like date,
                  persons, and contact info.
                </li>

                <li>
                  Submit booking and review updates
                  inside your dashboard.
                </li>
              </ol>
            </div>

            <div className="bookingStepsCard">
              <h3>Catering</h3>

              <ol>
                <li>
                  Open Catering and choose a package
                  or menu.
                </li>

                <li>
                  Enter event date, guest count, and
                  preferences.
                </li>

                <li>
                  Submit request and track status
                  from My Catering Bookings.
                </li>
              </ol>
            </div>

            <div className="bookingStepsCard">
              <h3>Madi Samayal Chef</h3>

              <ol>
                <li>
                  Open Chef Booking and choose chef
                  and time requirements.
                </li>

                <li>
                  Provide occasion details and
                  confirm booking info.
                </li>

                <li>
                  Submit and track updates from My
                  Chef Bookings.
                </li>
              </ol>
            </div>
          </div>
        </section>

        {/* ================= DASHBOARD GUIDE ================= */}

        <section className="section bookingStepsSection">
          <h2 className="sectionTitle">
            After Booking: Use Dashboard
          </h2>

          <p className="sectionSubtitle">
            Once you place any booking, use Dashboard
            to manage everything quickly.
          </p>

          <div className="bookingStepsGrid">
            <div className="bookingStepsCard">
              <h3>1. Open Dashboard</h3>

              <ol>
                <li>Login to your account.</li>

                <li>
                  Open Dashboard from the top
                  navigation.
                </li>

                <li>
                  Choose the relevant bookings
                  section.
                </li>
              </ol>
            </div>

            <div className="bookingStepsCard">
              <h3>2. Find Your Booking</h3>

              <ol>
                <li>
                  Use My Catering Bookings for event
                  orders.
                </li>

                <li>
                  Use My Chef Bookings for chef
                  requests.
                </li>

                <li>
                  Use Daily Food Orders for meal
                  subscriptions.
                </li>
              </ol>
            </div>

            <div className="bookingStepsCard">
              <h3>3. Track and Manage</h3>

              <ol>
                <li>
                  Check booking status and latest
                  updates.
                </li>

                <li>
                  View full details and order history
                  in one place.
                </li>

                <li>
                  Keep profile and contact details
                  updated.
                </li>
              </ol>
            </div>
          </div>
        </section>

        <Menus onGoToService={goToService} />
      </motion.main>

      {openHeroSlide && (
        <div
          className="hero-image-modal"
          role="dialog"
          aria-modal="true"
          aria-label={openHeroSlide.title}
          onClick={() => setOpenHeroSlide(null)}
        >
          <div
            className="hero-image-modal-content"
            onClick={event => event.stopPropagation()}
          >
            <button
              className="hero-image-modal-close"
              onClick={() => setOpenHeroSlide(null)}
              aria-label="Close Hero Image"
              type="button"
            >
              ×
            </button>

            <img
              src={openHeroSlide.image}
              alt={openHeroSlide.title}
              className="hero-image-modal-img"
            />
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default Home;
